import { convertISOToPicker } from 'js/components/common/Fields/DatePicker';
import { DATE_FIELD_DISPLAY_TYPE, FORM_FIELD_TYPE } from 'js/components/common/Form/constants';
import { TFormField, TFormFieldDropdown } from 'js/components/common/Form/types';
import { ICountryConfig } from 'js/components/system/App/store/types';
import {
    getCountriesList,
    getCurrenciesList,
    isValidDateString,
    validationRulesToValidationMethods
} from '@core/js/components/common/ChangeForm/helpers';
import {
    IValidationMethodConfigConfirm,
    IValidationMethodConfigRequired,
    TValidationMethodConfig
} from '@core/js/utils/Validation/types';
import { confirm, func, required } from '@core/js/utils/Validation/validationRules';
import { COMMON_REQUIRED_MESSAGE } from './constants';

export * from '@core/js/components/common/ChangeForm/helpers';

export const imsToFieldValue = (
    fieldValue: string | boolean,
    field: TFormField,
    additionalData: { allowedCountries?: ICountryConfig[]; allowedCurrencies?: string[] } = {}
): string | boolean | null => {
    const { allowedCurrencies, allowedCountries } = additionalData;

    switch (field.type) {
        case FORM_FIELD_TYPE.TEXT:
            if (field.imsField === 'citizenship') {
                const countryName = (allowedCountries as ICountryConfig[])
                    .find(el => el.code === fieldValue)?.name;
                if (countryName) {
                    return countryName;
                }
            }

            if (field.imsField === 'birthdate') {
                return (fieldValue as string).split('-').reverse()
                    .join('.');
            }

            return fieldValue;
        case FORM_FIELD_TYPE.CHECKBOX:
            if (field.imsValueChecked === fieldValue) {
                return true;
            } else if (field.imsValueUnchecked === fieldValue) {
                return false;
            }

            return null;
        case FORM_FIELD_TYPE.COUNTRY:
        case FORM_FIELD_TYPE.CURRENCY_CODE:
        case FORM_FIELD_TYPE.DROPDOWN: {
            const { selectedList, defaultValue, customList } = field as TFormFieldDropdown;

            const list = (() => {
                switch (selectedList) {
                    case 'countries': return getCountriesList(allowedCountries);
                    case 'currencies': return getCurrenciesList(allowedCurrencies);
                    default: return customList;
                }
            })();

            const possibleValues = list.map(({ value }) => value);

            return possibleValues.find(val => val === fieldValue)
                || possibleValues.find(val => val === defaultValue)
                || '';
        }
        case FORM_FIELD_TYPE.RADIO_BUTTON: {
            const { customList, defaultValue } = field;
            const possibleValues = customList.map(({ value }) => value);

            if (!possibleValues.includes(fieldValue as string)) {
                return defaultValue;
            }

            return fieldValue;
        }
        default: return fieldValue || '';
    }
};

export const fieldToImsValue = (
    formData: Record<string, string | boolean>,
    field: TFormField,
    allowedCountries: ICountryConfig[]
): string | boolean | null => {
    const fieldValue = formData[field.name] || '';

    switch (field.type) {
        case FORM_FIELD_TYPE.CHECKBOX:
            return fieldValue ? field.imsValueChecked || true : field.imsValueUnchecked || false;
        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            const { year, month, day } = convertISOToPicker(fieldValue);
            const areSomePartNotSet = [year, month, day]
                .map(part => parseInt(part))
                .some(part => isNaN(part));
            return areSomePartNotSet ? null : fieldValue;
        }
        case FORM_FIELD_TYPE.PHONE:
        case FORM_FIELD_TYPE.CELLPHONE: {
            const phoneCode = allowedCountries.find(country => country.code === formData[`${field.name}-areaCode`])?.phoneAreaCode || '';
            const phone = formData[field.name];

            return phone ? `${phoneCode}${phone}` : '';
        }
        case FORM_FIELD_TYPE.CITY_OF_BIRTH:
            /**
             * cityOfBirth value consists of city's italian <official code> and <city name> separated by "-". Example: "A896-BIVONA".
             * <official code> is used for italianId validation
             * <city name> should be sent to IMS
             */
            return (fieldValue as string)
                .split('-')
                .slice(1)
                .join('-');
        case FORM_FIELD_TYPE.TEXT:
            if (field.imsField === 'citizenship') {
                return (allowedCountries as ICountryConfig[]).find(el => el.name === fieldValue)?.code || '';
            }

            if (field.imsField === 'birthdate') {
                return (fieldValue as string).split('.').reverse()
                    .join('-');
            }

            return fieldValue;
        default: return fieldValue;
    }
};

export const getFieldValidations = (field: TFormField): Record<string, TValidationMethodConfig[]> => {
    const validations: Record<string, TValidationMethodConfig[]> = {};
    const { fetchUserDetails } = window.Playtech as any;
    const { lastName, firstName, username } = fetchUserDetails?.() as any;
    validations[field.name] = validationRulesToValidationMethods(field.validationRules, field.baseType);

    switch (field.type) {
        case FORM_FIELD_TYPE.TEXT:
        case FORM_FIELD_TYPE.EMAIL:
        case FORM_FIELD_TYPE.PASSWORD:
            if (field.requiresConfirmation) {
                validations[`${field.name}_confirmation`] = [
                    required({
                        error: COMMON_REQUIRED_MESSAGE,
                        message: COMMON_REQUIRED_MESSAGE
                    }) as IValidationMethodConfigRequired,
                    confirm({
                        field: field.name,
                        error: field.negativeTooltip
                    }) as IValidationMethodConfigConfirm
                ];
            }

            if (field.name === 'newPasswordTooltip') {
                validations[field.name].push(
                    func({
                        fn: (value: string) => {
                            const checkPassInvalid = () => {
                                return field && value.search(new RegExp((field as any), 'i')) > -1;
                            };

                            // password shouldn't contain  user first/last name or username
                            if (![firstName, lastName, username].some(checkPassInvalid)) {
                                return '';
                            }
                        }
                    }) as TValidationMethodConfig<string>
                );

                break;
            }

            break;

        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            validations[field.name].push(
                func({
                    fn: (value: string) => {
                        const { year: yearStr, month: monthStr, day: dayStr } = convertISOToPicker(value);
                        const [year, month, day] = [yearStr, monthStr, dayStr].map(part => parseInt(part));
                        const areAllInputsEmpty = [year, month, day].every(part => isNaN(part));
                        const areAllInputsFilled = [year, month, day].every(part => !isNaN(part));
                        const errorMessage = field.type === FORM_FIELD_TYPE.DATE_PICKER
                            ? 'playtech.mobile.system.form.datePicker.internal.required'
                            : 'playtech.mobile.system.form.birthdate.internal.required';

                        if (field.displayType === DATE_FIELD_DISPLAY_TYPE.INPUT) {
                            return isValidDateString(value) ? null : [errorMessage];
                        }

                        if (areAllInputsEmpty || areAllInputsFilled) {
                            return null;
                        }

                        return [errorMessage];
                    }
                }) as TValidationMethodConfig<string>
            );

            break;
        }
        case FORM_FIELD_TYPE.BET_LIMITS:
        case FORM_FIELD_TYPE.LOSS_LIMITS:
        case FORM_FIELD_TYPE.DEPOSIT_LIMITS:
            delete validations[field.name];
            break;
        default:
            break;
    }

    return validations;
};
